import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";

const initialForm = {
  run_id: "",
  member_id: "",
  title: "",
  vehicle: "",
  start_time: "",
  end_time: "",
  from_name: "",
  from_lat: "",
  from_lng: "",
  to_name: "",
  to_lat: "",
  to_lng: "",
};

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function valueOrEmpty(value) {
  return value === null || value === undefined ? "" : String(value);
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function toDatetimeLocal(value) {
  if (!value) return "";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function createPinIcon({ label, bg }) {
  return L.divIcon({
    className: "custom-map-pin-wrapper",
    html: `
      <div style="
        width:30px;height:30px;border-radius:50% 50% 50% 0;
        transform:rotate(-45deg);background:${bg};border:2px solid #fff;
        box-shadow:0 4px 10px rgba(0,0,0,.25);
        display:flex;align-items:center;justify-content:center;
      ">
        <span style="
          transform:rotate(45deg);font-size:12px;font-weight:700;color:#fff;
          font-family:Arial,sans-serif;
        ">${label}</span>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });
}

const fromIcon = createPinIcon({ label: "F", bg: "#16a34a" });
const toIcon = createPinIcon({ label: "T", bg: "#dc2626" });

export default function SentinelAddEscort() {
  const navigate = useNavigate();
  const location = useLocation();
  const skin = useSkin();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const editId = params.get("edit_id") || "";
  const isEdit = Boolean(editId);

  const [form, setForm] = useState(initialForm);
  const [guards, setGuards] = useState([]);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [activeTarget, setActiveTarget] = useState(null);
  const activeTargetRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [successMessage, setSuccessMessage] = useState(
    params.get("success") === "1" ? "Sentinel escort run created successfully!" : ""
  );
  const [errorMessage, setErrorMessage] = useState("");

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocoderRef = useRef(null);
  const fromMarkerRef = useRef(null);
  const toMarkerRef = useRef(null);
  const didFitBoundsRef = useRef(false);

  const sentinelRoutePage = useMemo(
    () =>
      dashboardData.user_map === "google"
        ? "/sentinel_add_Route"
        : "/sentinel_add_Route",
    [dashboardData.user_map]
  );

  const setTarget = (target) => {
    setActiveTarget(target);
    activeTargetRef.current = target;
  };

  const setLatLngOnTarget = useCallback((target, lat, lng, shouldCenter = false, label = "") => {
    if (!target) return;

    const latNum = Number(lat);
    const lngNum = Number(lng);

    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return;

    const lat7 = latNum.toFixed(7);
    const lng7 = lngNum.toFixed(7);

    if (target === "from") {
      setForm((prev) => ({
        ...prev,
        from_lat: lat7,
        from_lng: lng7,
        from_name: label || prev.from_name,
      }));
    }

    if (target === "to") {
      setForm((prev) => ({
        ...prev,
        to_lat: lat7,
        to_lng: lng7,
        to_name: label || prev.to_name,
      }));
    }

    if (shouldCenter && mapInstanceRef.current) {
      mapInstanceRef.current.invalidateSize(true);
      mapInstanceRef.current.setView([latNum, lngNum], 16);
    }
  }, []);

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    fetchPageData(token);
  }, [navigate, editId]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  useEffect(() => {
    if (loading) return;
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      zoomControl: true,
      attributionControl: true,
    });

    mapInstanceRef.current = map;

    map.setView([20.5937, 78.9629], 5);

    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);

    const geocoder = L.Control.geocoder({
      defaultMarkGeocode: false,
    })
      .on("markgeocode", (e) => {
        const target = activeTargetRef.current;

        if (!target) {
          setErrorMessage("Please click Pick on Map for From or To first.");
          return;
        }

        const center = e.geocode.center;

        if (e.geocode.bbox) {
          map.fitBounds(e.geocode.bbox);
        } else {
          map.setView(center, 16);
        }

        setLatLngOnTarget(target, center.lat, center.lng, true, e.geocode.name || "");
      })
      .addTo(map);

    geocoderRef.current = geocoder;

    map.on("click", (ev) => {
      const target = activeTargetRef.current;

      if (!target) {
        setErrorMessage("Please click Pick on Map for From or To first.");
        return;
      }

      setLatLngOnTarget(target, ev.latlng.lat, ev.latlng.lng, true);
    });

    const resizeMap = () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize(true);
      }
    };

    setTimeout(resizeMap, 100);
    setTimeout(resizeMap, 500);
    setTimeout(resizeMap, 1000);
    setTimeout(resizeMap, 1800);

    window.addEventListener("resize", resizeMap);

    return () => {
      window.removeEventListener("resize", resizeMap);
      map.remove();
      mapInstanceRef.current = null;
      geocoderRef.current = null;
      fromMarkerRef.current = null;
      toMarkerRef.current = null;
      didFitBoundsRef.current = false;
    };
  }, [loading, setLatLngOnTarget]);

  useEffect(() => {
    syncMarkers();
  }, [form.from_lat, form.from_lng, form.to_lat, form.to_lng, form.from_name, form.to_name]);

  async function fetchPageData(token) {
    try {
      setLoading(true);
      setErrorMessage("");

      const requests = [
        fetch(api("/api/dashboard/admin/dashboard"), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(api("/api/sentinel_escort/guards"), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ];

      if (editId) {
        requests.push(
          fetch(api(`/api/sentinel_escort/${editId}`), {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
      }

      const [dashboardRes, guardsRes, escortRes] = await Promise.all(requests);

      if (dashboardRes.ok) {
        const dashboardJson = await dashboardRes.json();
        const companyDetails = dashboardJson?.data?.companyDetails || {};
        setDashboardData((prev) => ({ ...prev, ...companyDetails }));
      }

      if (guardsRes.ok) {
        const guardsJson = await guardsRes.json();
        setGuards(guardsJson?.data?.guards || guardsJson?.data || []);
      }

      if (editId) {
        if (!escortRes?.ok) throw new Error("Failed to fetch sentinel escort run details");

        const escortJson = await escortRes.json();
        const run = escortJson?.data?.run || escortJson?.data || {};

        setForm({
          run_id: valueOrEmpty(run.run_id || run.sentinel_run_id || run.id || editId),
          member_id: valueOrEmpty(run.member_id || run.guard_id),
          title: valueOrEmpty(run.title),
          vehicle: valueOrEmpty(run.vehicle),
          start_time: toDatetimeLocal(run.start_time),
          end_time: toDatetimeLocal(run.end_time),
          from_name: valueOrEmpty(run.from_name),
          from_lat: valueOrEmpty(run.start_lat || run.from_lat),
          from_lng: valueOrEmpty(run.start_lng || run.from_lng),
          to_name: valueOrEmpty(run.to_name),
          to_lat: valueOrEmpty(run.end_lat || run.to_lat),
          to_lng: valueOrEmpty(run.end_lng || run.to_lng),
        });
      }
    } catch (error) {
      console.error("Sentinel add escort page error:", error);
      setErrorMessage(error.message || "Failed to load sentinel escort page.");
    } finally {
      setLoading(false);
    }
  }

  function syncMarkers() {
    const map = mapInstanceRef.current;
    if (!map) return;

    map.invalidateSize(true);

    const fLat = parseFloat(form.from_lat || "");
    const fLng = parseFloat(form.from_lng || "");
    const tLat = parseFloat(form.to_lat || "");
    const tLng = parseFloat(form.to_lng || "");

    if (Number.isFinite(fLat) && Number.isFinite(fLng)) {
      if (fromMarkerRef.current) map.removeLayer(fromMarkerRef.current);

      fromMarkerRef.current = L.marker([fLat, fLng], { icon: fromIcon })
        .addTo(map)
        .bindPopup(`From: ${form.from_name || "Selected location"}`);
    } else if (fromMarkerRef.current) {
      map.removeLayer(fromMarkerRef.current);
      fromMarkerRef.current = null;
    }

    if (Number.isFinite(tLat) && Number.isFinite(tLng)) {
      if (toMarkerRef.current) map.removeLayer(toMarkerRef.current);

      toMarkerRef.current = L.marker([tLat, tLng], { icon: toIcon })
        .addTo(map)
        .bindPopup(`To: ${form.to_name || "Selected location"}`);
    } else if (toMarkerRef.current) {
      map.removeLayer(toMarkerRef.current);
      toMarkerRef.current = null;
    }

    const layers = [fromMarkerRef.current, toMarkerRef.current].filter(Boolean);

    if (layers.length > 0) {
      const group = L.featureGroup(layers);
      map.fitBounds(group.getBounds().pad(0.25));
      didFitBoundsRef.current = true;
    } else if (!didFitBoundsRef.current) {
      map.setView([20.5937, 78.9629], 5);
    }
  }

  function geocodeAddress(target, text) {
    const geocoder = geocoderRef.current;

    if (!geocoder?._geocoder || !text.trim()) {
      setErrorMessage("Enter a location name first.");
      return;
    }

    setTarget(target);

    geocoder._geocoder.geocode(text, (results) => {
      if (!results?.[0]?.center) {
        setErrorMessage("Location not found.");
        return;
      }

      const center = results[0].center;
      setLatLngOnTarget(target, center.lat, center.lng, true, results[0].name || text);
    });
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function validateForm() {
    if (!form.member_id.trim()) return "Guard is required.";
    if (!form.title.trim()) return "Title is required.";
    if (!form.from_name.trim()) return "From location is required.";
    if (!form.to_name.trim()) return "To location is required.";
    if (!form.from_lat || !form.from_lng) return "Please set FROM location on map.";
    if (!form.to_lat || !form.to_lng) return "Please set TO location on map.";
    return "";
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      setSaving(true);
      setErrorMessage("");
      setSuccessMessage("");

      const token = getToken();

      const payload = {
        member_id: form.member_id.trim(),
        title: form.title.trim(),
        vehicle: form.vehicle.trim(),
        start_time: form.start_time,
        end_time: form.end_time,
        from_name: form.from_name.trim(),
        from_lat: form.from_lat,
        from_lng: form.from_lng,
        to_name: form.to_name.trim(),
        to_lat: form.to_lat,
        to_lng: form.to_lng,
      };

      const url = isEdit
        ? api(`/api/sentinel_escort/${form.run_id || editId}`)
        : api("/api/sentinel_escort");

      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save sentinel escort run");
      }

      if (isEdit) {
        setSuccessMessage("Sentinel escort run updated successfully!");
      } else {
        setForm(initialForm);
        navigate("/sentinel_escort_list", { replace: true });
      }
    } catch (error) {
      console.error("Save sentinel escort error:", error);
      setErrorMessage(error.message || "Failed to save sentinel escort run.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading sentinel escort management...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={sentinelRoutePage}
      activePage="sentinel-escort"
      activeMenu="sentinel-escort"
    >
      <PageHeader
        skin={skin}
        isEdit={isEdit}
        onRoute={() => navigate(sentinelRoutePage)}
        onList={() => navigate("/sentinel_escort_list")}
      />

      {successMessage ? <Alert type="success" message={successMessage} /> : null}
      {errorMessage ? <Alert type="error" message={errorMessage} /> : null}

      <FormSection
        title={isEdit ? "Edit Sentinel Escort Run" : "New Sentinel Escort Run"}
        subtitle="Create sentinel escort run and pick from/to coordinates directly from the map"
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Field label="Guard *">
              <Select
                name="member_id"
                value={form.member_id}
                onChange={handleChange}
                required
              >
                <option value="">— Select Guard —</option>
                {guards.map((guard, index) => {
                  const memberId =
                    guard.member_id ||
                    guard.guard_id ||
                    guard.fld_memberId ||
                    guard.fld_memberld ||
                    guard.memberId ||
                    "";

                  const label =
                    guard.label ||
                    guard.guard_name ||
                    guard.fld_name ||
                    guard.fld_username ||
                    guard.username ||
                    memberId;

                  return (
                    <option key={`${memberId}-${index}`} value={memberId}>
                      {label} ({memberId})
                    </option>
                  );
                })}
              </Select>
            </Field>

            <Field label="Title *">
              <Input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g., Coimbatore → Erode"
                required
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
            <Field label="Vehicle No.">
              <Input
                type="text"
                name="vehicle"
                value={form.vehicle}
                onChange={handleChange}
                placeholder="e.g., TN 66 AB 1234"
              />
            </Field>

            <Field label="Planned Start / End">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  type="datetime-local"
                  name="start_time"
                  value={form.start_time}
                  onChange={handleChange}
                />

                <Input
                  type="datetime-local"
                  name="end_time"
                  value={form.end_time}
                  onChange={handleChange}
                />
              </div>
            </Field>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
            <LocationField
              label="From *"
              name="from_name"
              value={form.from_name}
              lat={form.from_lat}
              lng={form.from_lng}
              onChange={handleChange}
              onPick={() => {
                setTarget("from");
                setErrorMessage("");
                setTimeout(() => mapInstanceRef.current?.invalidateSize(true), 100);
                window.alert("Click on the map or use the search box to set: FROM location");
              }}
              onSearch={() => geocodeAddress("from", form.from_name)}
            />

            <LocationField
              label="To *"
              name="to_name"
              value={form.to_name}
              lat={form.to_lat}
              lng={form.to_lng}
              onChange={handleChange}
              onPick={() => {
                setTarget("to");
                setErrorMessage("");
                setTimeout(() => mapInstanceRef.current?.invalidateSize(true), 100);
                window.alert("Click on the map or use the search box to set: TO location");
              }}
              onSearch={() => geocodeAddress("to", form.to_name)}
            />
          </div>

          <div className="mt-6">
            <button
              type="submit"
              disabled={saving}
              className="w-full px-5 py-3 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/20 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{
                background:
                  skin.gradient ||
                  "linear-gradient(135deg, #1e293b 0%, #2563eb 100%)",
                fontFamily: FONT_UI,
              }}
            >
              {saving
                ? "Saving..."
                : isEdit
                ? "Update Sentinel Escort Run"
                : "Save Sentinel Escort Run"}
            </button>
          </div>
        </form>
      </FormSection>

      <FormSection
        title="Sentinel Escort Map"
        subtitle={`Active picker: ${activeTarget ? activeTarget.toUpperCase() : "None"}`}
      >
        <div className="w-full h-[520px] min-h-[520px] relative overflow-hidden rounded-2xl bg-slate-200 z-[1] border border-slate-200">
          <div
            ref={mapRef}
            id="sentinel-escort-leaflet-map"
            className="w-full h-[520px] min-h-[520px] block relative z-[1] rounded-2xl overflow-hidden bg-slate-200"
          />
        </div>
      </FormSection>

      <style>{`
        #sentinel-escort-leaflet-map .leaflet-container,
        .leaflet-container {
          width: 100% !important;
          height: 520px !important;
          min-height: 520px !important;
          display: block !important;
          background: #e5e7eb !important;
          z-index: 1 !important;
        }

        .leaflet-pane {
          z-index: 2 !important;
        }

        .leaflet-top,
        .leaflet-bottom {
          z-index: 10 !important;
        }

        .leaflet-control-container {
          z-index: 20 !important;
        }

        .custom-map-pin-wrapper {
          background: transparent !important;
          border: none !important;
        }
      `}</style>
    </Layout>
  );
}

function PageHeader({ skin, isEdit, onRoute, onList }) {
  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Sentinel Escort Run
        </h2>

        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {isEdit ? "Edit an existing sentinel escort run" : "Create a new sentinel escort run"}
        </p>
      </div>

      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6 flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h3
            className="m-0 text-[22px] font-normal text-slate-900"
            style={{ fontFamily: FONT_HEADING }}
          >
            {isEdit ? "Edit Sentinel Escort" : "New Sentinel Escort"}
          </h3>

          <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            Manage guard, title, vehicle, planned time, and route points
          </p>
        </div>

        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={onRoute}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/20"
            style={{
              background:
                skin.gradient ||
                "linear-gradient(135deg, #1e293b 0%, #2563eb 100%)",
              fontFamily: FONT_UI,
            }}
          >
            Sentinel Route
          </button>

          <button
            type="button"
            onClick={onList}
            className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            style={{ fontFamily: FONT_UI }}
          >
            Escort List
          </button>
        </div>
      </div>
    </>
  );
}

function LocationField({ label, name, value, lat, lng, onChange, onPick, onSearch }) {
  return (
    <Field label={label}>
      <div className="grid gap-3">
        <Input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSearch();
            }
          }}
          placeholder="Search or click on map"
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input type="text" value={lat} readOnly placeholder="Lat" />
          <Input type="text" value={lng} readOnly placeholder="Lng" />
        </div>

        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={onPick}
            className="px-4 py-2.5 rounded-xl bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600"
            style={{ fontFamily: FONT_UI }}
          >
            Pick on Map
          </button>

          <button
            type="button"
            onClick={onSearch}
            className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            style={{ fontFamily: FONT_UI }}
          >
            Search Location
          </button>
        </div>
      </div>
    </Field>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label
        className="block mb-2 text-sm font-semibold text-slate-900"
        style={{ fontFamily: FONT_UI }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white box-border focus:ring-2 focus:ring-blue-100 focus:border-blue-500 ${className}`}
      style={{ fontFamily: FONT_UI }}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white box-border focus:ring-2 focus:ring-blue-100 focus:border-blue-500 ${className}`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </select>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border ${
        isSuccess
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function FormSection({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6">
      <div className="mb-5">
        <h3
          className="m-0 text-[22px] font-normal text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          {title}
        </h3>

        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {subtitle}
        </p>
      </div>

      {children}
    </div>
  );
}